import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuthStatus } from "../hooks/useAuthStatus";
import { useAuth } from "../hooks/useAuth";
import LoadingScreen from '../../../app/components/layout/LoadingScreen';
import { getAccessToken, getRefreshToken, setAccessToken, setRefreshToken, ensureCurrentUserTokens } from '../../../app/utils/authStorage';

const AuthGuard = ({ children }) => {
    const { isLoading, isAuthenticated } = useAuthStatus();
    const { loginWithTelegram } = useAuth();
    const [authAttempting, setAuthAttempting] = useState(false);
    const lastAuthAttemptRef = useRef(0);
    const inMiniApp = window?.Telegram?.WebApp?.initData;

    const handleTokenRefresh = useCallback(async () => {
        const refreshToken = getRefreshToken();

        if (!refreshToken) {
            return false;
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_AUTH_SERVICE_URL}/v1/refresh`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ refresh_token: refreshToken }),
            });

            if (response.ok) {
                const data = await response.json();
                setAccessToken(data.access_token);
                setRefreshToken(data.refresh_token);
                return true;
            }
        } catch (err) {
            console.error('Token refresh failed:', err);
        }

        return false;
    }, []);

    const checkAndRefreshToken = useCallback(async () => {
        // Предотвращаем множественные одновременные попытки
        const now = Date.now();
        if (authAttempting || (now - lastAuthAttemptRef.current < 2000)) {
            return;
        }

        setAuthAttempting(true);
        lastAuthAttemptRef.current = now;

        try {
            ensureCurrentUserTokens();

            if (getAccessToken()) {
                return; // Токен есть, все хорошо
            }

            // Приоритет 1: Если в MiniApp, пытаемся авторизоваться через initData
            if (inMiniApp) {
                console.log('[AuthGuard] Attempting MiniApp auth via initData');
                try {
                    await loginWithTelegram();
                    console.log('[AuthGuard] MiniApp auth successful');
                    return;
                } catch (err) {
                    console.error('[AuthGuard] MiniApp auth failed:', err);
                }
            }

            // Приоритет 2: Пробуем обновить токен через refresh token
            const refreshed = await handleTokenRefresh();
            if (refreshed) {
                console.log('[AuthGuard] Token refresh successful');
            }
        } finally {
            setAuthAttempting(false);
        }
    }, [handleTokenRefresh, loginWithTelegram, inMiniApp, authAttempting]);

    useEffect(() => {
        if (!isLoading && !isAuthenticated) {
            checkAndRefreshToken();
        }
    }, [isLoading, isAuthenticated, checkAndRefreshToken]);

    // В MiniApp постоянно проверяем наличие токена и пытаемся авторизоваться при его отсутствии
    useEffect(() => {
        if (!inMiniApp) return;

        const interval = setInterval(() => {
            if (!getAccessToken() && !authAttempting) {
                console.log('[AuthGuard] Token lost in MiniApp, attempting re-auth');
                checkAndRefreshToken();
            }
        }, 3000);

        return () => clearInterval(interval);
    }, [inMiniApp, checkAndRefreshToken, authAttempting]);

    if (getAccessToken()) return children;

    if (isLoading || authAttempting) {
        return <LoadingScreen text="Ждем авторизации..." />;
    }

    if (!isAuthenticated && !isLoading) {
        return <LoadingScreen text="Авторизация..." />;
    }

    return children;
};

export default AuthGuard;
